import { useCallback, useState } from 'react';

import { useHHAExchangeAcceptanceNoteReasons } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type components } from '@libraries/typescript/api_client';

type AcceptanceNoteReason =
    components['schemas']['HHAExchangeAcceptanceNoteReason'];

export type HHAExchangeAcceptanceNoteDecision = NonNullable<
    components['schemas']['ShiftRateInfo']['hhaexchange_acceptance_note']
>;

type ManualAcceptanceNoteDecision = Exclude<
    HHAExchangeAcceptanceNoteDecision,
    { readonly action: 'automatic' }
>;

type AcceptanceNoteReasonFieldState = {
    readonly shouldRender: boolean;
    readonly reasons: ReadonlyArray<AcceptanceNoteReason> | undefined;
    readonly selectedReasonId: number | null;
    readonly isLoading: boolean;
    readonly error: unknown;
    readonly reasonError: string | null | undefined;
};

type UseHHAExchangeAcceptanceNoteDecisionsParams = {
    readonly enabled: boolean;
    readonly organizationId: string | null;
    readonly outreachId: string;
    readonly shiftIds: Array<string>;
};

export function useHHAExchangeAcceptanceNoteDecisions({
    enabled,
    organizationId,
    outreachId,
    shiftIds,
}: UseHHAExchangeAcceptanceNoteDecisionsParams) {
    const { data, isLoading, error } = useHHAExchangeAcceptanceNoteReasons(
        enabled ? organizationId : null,
        enabled ? outreachId : null,
        { reason: 'AssignAndFillConfirmationModal' },
        enabled ? shiftIds : undefined,
    );
    const [manualDecisionsByShiftId, setManualDecisionsByShiftId] = useState<
        Map<string, ManualAcceptanceNoteDecision>
    >(() => new Map());

    const setReason = useCallback(
        (shiftId: string, reasonId: number | null) => {
            setManualDecisionsByShiftId((previous) => {
                const next = new Map(previous);
                next.set(
                    shiftId,
                    reasonId === null
                        ? { action: 'skip' }
                        : { action: 'selected_reason', reason_id: reasonId },
                );
                return next;
            });
        },
        [],
    );

    const reset = useCallback(() => {
        setManualDecisionsByShiftId(new Map());
    }, []);

    const getDecision = useCallback(
        (shiftId: string): HHAExchangeAcceptanceNoteDecision => {
            const manualDecision = manualDecisionsByShiftId.get(shiftId);
            if (manualDecision) {
                return manualDecision;
            }
            const defaultReasonId =
                data?.reasons_by_shift?.[shiftId]?.default_reason_id;
            if (defaultReasonId !== null && defaultReasonId !== undefined) {
                return {
                    action: 'selected_reason',
                    reason_id: defaultReasonId,
                };
            }
            if (
                enabled &&
                (isLoading || Boolean(error) || data?.writeback_enabled)
            ) {
                return { action: 'skip' };
            }
            return { action: 'automatic' };
        },
        [data, enabled, error, isLoading, manualDecisionsByShiftId],
    );

    const getReasonFieldState = useCallback(
        (shiftId: string): AcceptanceNoteReasonFieldState => {
            const reasonInfo = data?.reasons_by_shift?.[shiftId];
            const defaultReasonIsMissing =
                reasonInfo?.default_reason_id === null ||
                reasonInfo?.default_reason_id === undefined;
            const manualDecision = manualDecisionsByShiftId.get(shiftId);
            return {
                shouldRender:
                    enabled &&
                    (Boolean(error) ||
                        (data?.writeback_enabled === true &&
                            defaultReasonIsMissing)),
                reasons: reasonInfo?.reasons,
                selectedReasonId:
                    manualDecision?.action === 'selected_reason'
                        ? manualDecision.reason_id
                        : null,
                isLoading,
                error,
                reasonError: reasonInfo?.error,
            };
        },
        [data, enabled, error, isLoading, manualDecisionsByShiftId],
    );

    return {
        getDecision,
        getReasonFieldState,
        reset,
        setReason,
    };
}
